import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { debounce } from '@theme/utilities';

/**
 * Blog posts directory with tag filters, in-section search, and AJAX pagination.
 */
class BlogPostsDirectory extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#filterSearch();
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  /**
   * @param {{ tag?: string, page?: number }} options
   * @returns {URL}
   */
  #buildBlogUrl({ tag = '', page = 1 } = {}) {
    const handle = this.dataset.blogHandle;
    if (!handle) return new URL(window.location.href);

    let path = `/blogs/${handle}`;
    if (tag) path += `/tagged/${tag}`;

    const url = new URL(path, window.location.origin);
    if (page > 1) url.searchParams.set('page', String(page));

    return url;
  }

  /**
   * @param {Event | { tag?: string }} params
   * @param {Event} [event]
   */
  applyTag = async (params, event) => {
    if (params instanceof Event) {
      event = params;
      params = {};
    }

    event?.preventDefault();

    const tag = typeof params.tag === 'string' ? params.tag : '';
    const url = this.#buildBlogUrl({ tag, page: 1 });

    await sectionRenderer.renderSection(this.sectionId, { cache: false, url });
    this.#clearSearchInput();
    this.#scrollToGrid();
  };

  /**
   * @param {Event | { page?: string | number }} params
   * @param {Event} [event]
   */
  handlePagination = async (params, event) => {
    if (params instanceof Event) {
      event = params;
      params = {};
    }

    event?.preventDefault();

    const page = Number(params.page) || 1;
    const tag = this.dataset.activeTag || '';
    const url = this.#buildBlogUrl({ tag, page });

    await sectionRenderer.renderSection(this.sectionId, { cache: false, url });
    this.#clearSearchInput();
    this.#scrollToGrid();
  };

  filterSearch = debounce(() => {
    this.#filterSearch();
  }, 200);

  clearSearch = (event) => {
    event.preventDefault();
    this.refs.searchInput.value = '';
    this.#filterSearch();
    this.refs.searchInput.focus();
  };

  #clearSearchInput() {
    if (this.refs.searchInput) {
      this.refs.searchInput.value = '';
    }
    this.#filterSearch();
  }

  #filterSearch() {
    const query = (this.refs.searchInput?.value ?? '').trim().toLowerCase();
    const cards = this.refs.cards;
    const cardList = Array.isArray(cards) ? cards : cards ? [cards] : [];

    let visibleCount = 0;

    for (const card of cardList) {
      const searchText = card.dataset.searchText ?? '';
      const isMatch = !query || searchText.includes(query);
      card.hidden = !isMatch;
      if (isMatch) visibleCount += 1;
    }

    if (this.refs.emptyState) {
      this.refs.emptyState.hidden = visibleCount > 0 || !query;
    }

    if (this.refs.pagination) {
      this.refs.pagination.hidden = query.length > 0;
    }

    if (this.refs.resultsSummary) {
      this.refs.resultsSummary.hidden = !query;
      this.refs.resultsSummary.textContent = visibleCount
        ? `${visibleCount} result${visibleCount === 1 ? '' : 's'} on this page`
        : 'No matching posts on this page';
    }
  }

  #scrollToGrid() {
    this.refs.grid?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

if (!customElements.get('blog-posts-directory')) {
  customElements.define('blog-posts-directory', BlogPostsDirectory);
}
